import React, { useState, useRef } from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Checkbox,
  FormControlLabel,
  Alert,
  IconButton,
  Box,
  Typography,
  Fade
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import api from '../../api';
import languageLearningApi from "../language_learning/middleware/languageLearningApi";
import { styled } from '@mui/material/styles';
import { useNavigation } from '../../context/NavigationContext.js';

// Custom styled components
const KnowledgeButton = styled(Button)(({ theme }) => ({
  // Removed absolute positioning to work within flexbox container
  backgroundColor: 'rgba(255, 255, 255, 0.9)',
  color: '#1a237e',
  fontWeight: 600,
  borderRadius: '20px',
  padding: '8px 16px',
  borderColor: '#1a237e',
  boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
  textTransform: 'none',
  minWidth: '160px', // Ensure sufficient width
  whiteSpace: 'nowrap', // Prevent text wrapping
  zIndex: 10, // Keep zIndex without absolute positioning
  '&:hover': {
    backgroundColor: '#e8eaf6',
    borderColor: '#3949ab',
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    transition: 'all 0.2s ease'
  },
  transition: 'all 0.2s ease'
}));

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiPaper-root': {
    borderRadius: '12px',
    boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
    overflow: 'hidden'
  }
}));

const DialogHeader = styled(DialogTitle)(({ theme }) => ({
  backgroundColor: '#1a237e',
  color: 'white',
  padding: '16px 24px',
  fontWeight: 600
}));

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  padding: '24px',
  backgroundColor: 'white'
}));

const StyledDialogActions = styled(DialogActions)(({ theme }) => ({
  padding: '16px 24px',
  borderTop: '1px solid #f0f0f0',
  backgroundColor: 'white'
}));

const CancelButton = styled(Button)(({ theme }) => ({
  borderRadius: '20px',
  textTransform: 'none',
  padding: '8px 20px',
  fontWeight: 500
}));

const ConfirmButton = styled(Button)(({ theme }) => ({
  borderRadius: '20px',
  textTransform: 'none',
  padding: '8px 20px',
  fontWeight: 600,
  backgroundColor: '#1a237e',
  '&:hover': {
    backgroundColor: '#3949ab'
  }
}));

const StyledAlert = styled(Alert)(({ theme, severity }) => ({
  position: 'fixed',
  top: 20,
  right: 20,
  zIndex: 2000,
  width: '400px',
  maxWidth: '90%',
  borderRadius: '12px',
  boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
  backgroundColor: severity === 'error' ? '#FFF4F4' : '#EEFFEF',
  padding: '16px',
  '& .MuiAlert-message': {
    width: '100%'
  },
  '& .MuiAlert-icon': {
    color: severity === 'error' ? '#f44336' : '#4caf50'
  }
}));

const StyledCheckbox = styled(Checkbox)(({ theme }) => ({
  '&.Mui-checked': {
    color: '#1a237e'
  }
}));

const CheckboxLabel = styled(FormControlLabel)(({ theme }) => ({
  marginTop: '16px',
  '& .MuiFormControlLabel-label': {
    fontSize: '0.9rem',
    color: '#666'
  }
}));

const ResultItem = styled('li')(({ theme }) => ({
  margin: '8px 0',
  padding: '6px 0',
  borderBottom: '1px solid #f0f0f0',
  '&:last-child': {
    borderBottom: 'none'
  }
}));

const SuccessHeader = styled(Typography)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  fontWeight: 600,
  marginBottom: '12px'
}));

const ResultList = styled('ul')(({ theme }) => ({
  paddingLeft: '8px',
  margin: '10px 0',
  listStyleType: 'none'
}));

const AlreadyKnowButton = ({
  cardId,
  deckId,
  sentenceId,
  userId,
  courseId,
  onBurnComplete 
}) => {
  const [open, setOpen] = useState(false);
  const [dontAskAgain, setDontAskAgain] = useState(false);
  const [skipConfirmation, setSkipConfirmation] = useState(false);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertContent, setAlertContent] = useState({});

  const { currentCourse } = useNavigation();

  const isBurningRef = useRef(false);

  const createFlashcardMapping = async (mappingData) => {
    try {
      await languageLearningApi.post('/flashcard-mappings', mappingData);
      console.log('Created flashcard mapping:', mappingData);
      return true;
    } catch (error) {
      if (error.response && error.response.status === 409) {
        console.log('Mapping already exists (409):', mappingData);
        return true;
      }
      console.error('Error creating flashcard mapping:', error);
      return false;
    }
  };

  const handleButtonClick = () => {
    if (skipConfirmation) {
      handleBurnCard();
    } else {
      setOpen(true);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setDontAskAgain(false);
  };

  const handleBurnCard = async () => {
    if (isBurningRef.current) {
      console.log("Burn operation already in progress.");
      return;
    }
    isBurningRef.current = true;

    try {
      if (open) {
        setOpen(false);
      }

      if (dontAskAgain) {
        setSkipConfirmation(true);
      }

      const payload = {
        cardId,
        deckId,
        ...(sentenceId && { sentenceId })
      };

      const response = await api.post('/burn-item', payload);
      const results = response.data.results;

      const processedConcepts = new Set();

      if (sentenceId) {
        await createFlashcardMapping({
          userId,
          courseId,
          sentenceId,
          conceptName: null,
          language: currentCourse.language
        });
      }

      const burnPromises = Object.entries(results)
        .filter(([id, result]) => result.includes("Burned successfully"))
        .map(async ([id, result]) => {
          let conceptName = null;
          if (result.includes("(vocabulary:")) {
            conceptName = result.match(/\(vocabulary: (.*?)\)/)?.[1];
          } else if (result.includes("(grammar:")) {
            conceptName = result.match(/\(grammar: (.*?)\)/)?.[1];
          }

          console.log('Detected concept name:', conceptName);

          if (courseId && conceptName && !processedConcepts.has(conceptName)) {
            processedConcepts.add(conceptName);

            await createFlashcardMapping({
              userId,
              courseId,
              sentenceId: null,
              conceptName,
              language: currentCourse.language
            });
          }
        });

      await Promise.all(burnPromises);

      const alertItems = Object.entries(results).map(([id, result]) => ({
        id,
        message: result
      }));

      setAlertContent({
        items: alertItems,
        mainCardId: response.data.mainCardId
      });
      setAlertOpen(true);

      if (onBurnComplete && typeof onBurnComplete === 'function') {
        const burntCardIds = Object.entries(results)
          .filter(([id, result]) => result.includes("Burned successfully"))
          .map(([id]) => id);

        if (!burntCardIds.includes(cardId)) {
          burntCardIds.unshift(cardId);
        }

        onBurnComplete(burntCardIds, results);
      }

      setTimeout(() => {
        setAlertOpen(false);
      }, 2000 + 500 * Object.keys(results).length);

    } catch (error) {
      console.error("Error burning card:", error);
      setAlertContent({
        error: true,
        message: "Error burning card. Please try again."
      });
      setAlertOpen(true);
      setTimeout(() => {
        setAlertOpen(false);
      }, 3000);
    } finally {
      isBurningRef.current = false;
    }
  };

  return (
    <>
      <KnowledgeButton
        variant="outlined"
        onClick={handleButtonClick}
        startIcon={<CheckCircleIcon />}
      >
        I already know this
      </KnowledgeButton>

      {/* Confirmation Dialog */}
      <StyledDialog
        open={open}
        onClose={handleClose}
        aria-labelledby="burn-card-dialog-title"
        TransitionComponent={Fade}
        transitionDuration={300}
      >
        <DialogHeader id="burn-card-dialog-title">
          Mark as Already Known
        </DialogHeader>
        <StyledDialogContent>
          <br />
          <DialogContentText sx={{ color: '#444', fontSize: '1rem' }}>
            Do you want to mark this item as learned? This item, and all its dependent concepts will never be shown again in learning/review sessions.
          </DialogContentText>
          <CheckboxLabel
            control={
              <StyledCheckbox
                checked={dontAskAgain}
                onChange={(e) => setDontAskAgain(e.target.checked)}
              />
            }
            label="Don't ask me again in this session"
          />
        </StyledDialogContent>
        <StyledDialogActions>
          <CancelButton onClick={handleClose} color="inherit">
            Cancel
          </CancelButton>
          <ConfirmButton onClick={handleBurnCard} color="primary" variant="contained">
            Yes, I Know This
          </ConfirmButton>
        </StyledDialogActions>
      </StyledDialog>

      {/* Success/Error Alert */}
      {alertOpen && (
        <Fade in={alertOpen}>
          <StyledAlert
            severity={alertContent.error ? "error" : "success"}
            icon={alertContent.error ? undefined : <CheckCircleIcon fontSize="inherit" />}
            action={
              <IconButton
                aria-label="close"
                color="inherit"
                size="small"
                onClick={() => setAlertOpen(false)}
              >
                <CloseIcon fontSize="inherit" />
              </IconButton>
            }
          >
            {alertContent.error ? (
              alertContent.message
            ) : (
              <>
                <SuccessHeader variant="h6">
                  <CheckCircleIcon /> Burn operation completed
                </SuccessHeader>
                <ResultList>
                  {alertContent.items?.map((item) => (
                    <ResultItem key={item.id}>{item.message}</ResultItem>
                  ))}
                </ResultList>
                <Typography variant="body2" sx={{ fontStyle: 'italic', color: '#666', mt: 1 }}>
                  Please skip through the cards burnt to continue learning
                </Typography>
              </>
            )}
          </StyledAlert>
        </Fade>
      )}
    </>
  );
};

export default AlreadyKnowButton;